import AddIcon from '@mui/icons-material/Add';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { dayPartApi, dayPartKeys } from '@/api/account-management/dayPartApi';
import { type DayPart } from '@/models/account-management/DayPart';
import { minuteToTime } from '@/models/media-network/PlaylistSchedule';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import { fillHeight } from '../../theme';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import Spacer from '../common/Spacer';
import { useToast } from '../common/toast/ToastProvider';
import DayPartDialog from './dialogs/DayPartDialog';

const DayParts = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.configurationDayParts);
    const canUpdate = perms.canUpdate(Perm.configurationDayParts);
    const canDelete = perms.canDelete(Perm.configurationDayParts);

    const brandId = app.brandId ?? undefined;

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<DayPart | null>(null);

    const { data: dayParts, isPending } = useQuery<DayPart[]>({
        queryKey: dayPartKeys.list(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => dayPartApi.list(brandId!),
        staleTime: 0,
    });

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => dayPartApi.remove(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.dayPartDeleted });
            await qc.invalidateQueries({ queryKey: dayPartKeys.list(brandId ?? '') });
            // A schedule that references a day part renders its window from it, so those lists are stale too.
            await qc.invalidateQueries({ queryKey: ['promotion-schedules'] });
        },
        onError: () => toast({ severity: 'error', message: strings.dayPartDeleteError }),
    });

    const columns: GridColDef<DayPart>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        {
            field: 'window', headerName: strings.dayPartWindow, flex: 2,
            // end < start is not an error — it means the window runs past midnight, which is exactly what a
            // "Late Night" day part is for.
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignItems="center" sx={{ height: '100%' }}>
                    <Typography variant="body2">
                        {minuteToTime(params.row.startMinute)}–{minuteToTime(params.row.endMinute)}
                    </Typography>
                    {params.row.endMinute < params.row.startMinute && (
                        <Chip size="small" variant="outlined" label={strings.dayPartOvernight} />
                    )}
                </Stack>
            ),
        },
        {
            field: 'locationOverrides', headerName: strings.dayPartOverrides, flex: 1,
            valueGetter: (_value, row) => row.locationOverrides?.length ?? 0,
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<DayPart>) => [
                <DataGridEditButton
                    id={params.id.toString()}
                    onClick={() => { setEditing(params.row); setDialogOpen(true); }}
                    aria-label="edit day part"
                    readOnly={!canUpdate}
                />,
                <DataGridDeleteButton
                    id={params.id.toString()}
                    getWarningMessage={(id) => strings.dayPartDeleteConfirm.replace('{{name}}', dayParts?.find(d => d.id === id)?.name ?? '')}
                    onClick={(id) => remove(id)}
                    aria-label="delete day part"
                    hidden={!canDelete}
                />,
            ],
        },
    ];

    if (!brandId) {
        return (
            <Box sx={{ ...fillHeight, p: 2 }}>
                <Typography variant="body2" color="text.secondary">{strings.dayPartSelectBrandTopBar}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ ...fillHeight, overflow: 'hidden', display: 'flex', flexDirection: 'column', p: 2 }}>
            {/* No page heading - the title bar breadcrumb already says "Day Parts". The help text takes that
                row instead, so the reminder that these are shared sits where the eye already goes rather than
                below the fold.

                Day parts are brand-level and shared, so it's worth saying that editing one moves every
                schedule built on it — that is the point of them, and also the way to break things. */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
                <Alert severity="info" variant="outlined" sx={{ flex: 1, py: 0.25 }}>{strings.dayPartHelp}</Alert>
                {canCreate && (
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => { setEditing(null); setDialogOpen(true); }}
                        size="input"
                        sx={{ flexShrink: 0 }}
                    >
                        {strings.dayPartNew}
                    </Button>
                )}
            </Stack>

            <Box sx={{ flex: 1, minHeight: 0, minWidth: 0 }}>
                <DataGrid
                    rows={dayParts ?? []}
                    columns={columns}
                    loading={isPending}
                    getRowId={(row) => row.id}
                    disableRowSelectionOnClick
                />
            </Box>

            {/* Editing is only reachable through the grid's eye/pencil, so read-only follows canUpdate. A
                new day part is behind canCreate, and `editing` is null then, so this stays false. */}
            <DayPartDialog
                open={dialogOpen}
                brandId={brandId}
                dayPart={editing}
                readOnly={!!editing && !canUpdate}
                onClose={() => setDialogOpen(false)}
            />
        </Box>
    );
};

export default DayParts;
